import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, Select, Switch } from '@attendance/ui';
import type {
  ReportCadence,
  ReportSchedule,
  ReportType,
} from '@attendance/shared/schemas/settings';
import { randomUuid } from '../../lib/uuid.js';

// ---------------------------------------------------------------------------
// Scheduled reports — the one setting that is a list rather than a value.
// Extracted from SettingsControls.tsx, which is over its line budget: this is
// the only control in the family that is a repeating editor rather than one
// box, and it was two thirds of that file.
// The whole array is the patch body; `deepMerge` replaces arrays wholesale,
// which is the only correct reading of "the list is now this".
// ---------------------------------------------------------------------------

const REPORT_TYPE_OPTIONS: ReportType[] = [
  'attendance-summary',
  'timesheet',
  'document-expiry',
  'leave-balance',
];

const CADENCE_OPTIONS: ReportCadence[] = ['daily', 'weekly', 'monthly'];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function parseRecipients(raw: string): {
  valid: string[];
  invalid: string[];
} {
  const valid = new Set<string>();
  const invalid = new Set<string>();
  for (const piece of raw.split(/[,\s;]+/)) {
    const trimmed = piece.trim();
    if (!trimmed) continue;
    if (EMAIL_RE.test(trimmed)) valid.add(trimmed);
    else invalid.add(trimmed);
  }
  return { valid: Array.from(valid), invalid: Array.from(invalid) };
}

function newSchedule(): ReportSchedule {
  const id =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? randomUuid()
      : `sched-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
  return {
    id,
    reportType: 'attendance-summary',
    cadence: 'daily',
    recipients: [],
    enabled: true,
  };
}

export function ReportSchedulesEditor({
  value,
  onChange,
}: {
  value: readonly ReportSchedule[];
  onChange: (next: ReportSchedule[]) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const list = value ?? [];

  function update(index: number, patch: Partial<ReportSchedule>): void {
    onChange(list.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  }

  return (
    <div className="space-y-component">
      {list.length === 0 && (
        <p className="text-copy text-muted-foreground">
          {t('settings.schedules.empty')}
        </p>
      )}
      {list.map((schedule, index) => (
        <ScheduleRow
          key={schedule.id}
          schedule={schedule}
          index={index}
          onChange={(patch) => update(index, patch)}
          onRemove={() => onChange(list.filter((_, i) => i !== index))}
        />
      ))}
      <Button
        type="button"
        variant="outline"
        size="touch"
        onClick={() => onChange([...list, newSchedule()])}
      >
        {t('settings.schedules.add')}
      </Button>
    </div>
  );
}

function ScheduleRow({
  schedule,
  index,
  onChange,
  onRemove,
}: {
  schedule: ReportSchedule;
  index: number;
  onChange: (patch: Partial<ReportSchedule>) => void;
  onRemove: () => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [text, setText] = useState(schedule.recipients.join(', '));
  const [invalid, setInvalid] = useState<string[]>([]);
  const prefix = `schedule-${schedule.id}`;

  function commit(raw: string): void {
    const parsed = parseRecipients(raw);
    setInvalid(parsed.invalid);
    const same =
      parsed.valid.length === schedule.recipients.length &&
      parsed.valid.every((v) => schedule.recipients.includes(v));
    if (!same) onChange({ recipients: parsed.valid });
  }

  return (
    // `rounded-card` (12px) — the INNER-card role, and correct: a schedule row is
    // one repeated block inside the schedules field, which is inside a
    // `SectionShell` (`rounded-panel`). It is not a card of its own.
    <div className="rounded-card border border-border p-4">
      <div className="flex flex-wrap items-center justify-between gap-element">
        <p className="text-label text-foreground">
          {t('settings.schedules.row', { n: index + 1 })}
        </p>
        {/* `size="touch"` rather than `sm` + a hand-written `min-h-touch`.
            `sm` also carries `text-badge`, the deprecated 11px step §1 of the
            rules bans for anything read as a word, so the override was buying
            the height back while leaving the type below the floor. */}
        <Button type="button" variant="outline" size="touch" onClick={onRemove}>
          {t('settings.schedules.remove')}
        </Button>
      </div>
      <div className="mt-3 grid gap-inline sm:grid-cols-2">
        <div className="space-y-element">
          <Label htmlFor={`${prefix}-type`}>{t('settings.schedules.report')}</Label>
          <Select
            id={`${prefix}-type`}
            value={schedule.reportType}
            onChange={(e) => onChange({ reportType: e.target.value as ReportType })}
          >
            {REPORT_TYPE_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {t(`settings.schedules.type.${o}`)}
              </option>
            ))}
          </Select>
        </div>
        <div className="space-y-element">
          <Label htmlFor={`${prefix}-cadence`}>
            {t('settings.schedules.cadence')}
          </Label>
          <Select
            id={`${prefix}-cadence`}
            value={schedule.cadence}
            onChange={(e) => onChange({ cadence: e.target.value as ReportCadence })}
          >
            {CADENCE_OPTIONS.map((o) => (
              <option key={o} value={o}>
                {t(`settings.schedules.every.${o}`)}
              </option>
            ))}
          </Select>
        </div>
      </div>
      <div className="mt-3 space-y-element">
        <Label htmlFor={`${prefix}-recipients`}>
          {t('settings.schedules.recipients')}
        </Label>
        <Input
          id={`${prefix}-recipients`}
          value={text}
          placeholder="hr@example.com, payroll@example.com"
          onChange={(e) => {
            setText(e.target.value);
            commit(e.target.value);
          }}
          onBlur={() => commit(text)}
        />
        {invalid.length > 0 && (
          <p className="text-copy-sm text-danger" role="alert">
            {t('settings.schedules.badEmail', { list: invalid.join(', ') })}
          </p>
        )}
      </div>
      <label
        htmlFor={`${prefix}-enabled`}
        className="mt-3 flex min-h-touch cursor-pointer items-center gap-element text-copy"
      >
        <Switch
          id={`${prefix}-enabled`}
          checked={schedule.enabled}
          onChange={(e) => onChange({ enabled: e.target.checked })}
        />
        {t('settings.schedules.enabled')}
      </label>
    </div>
  );
}
